// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Ticket Master lane ④ "FAV calls" (Leland 2026-10-28) — one FAV call, in words. Pure, for the
// timeline cell and the 🤖 conclusion. `raw` = a row from Convex scheduler/fav2Log:forJob.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const SKIP_WORDS = {
  degraded: "the schedule data was incomplete on that call",
  no_job: "the job couldn't be read",
  unknown_floor_type: "unknown floor type",
  no_job_estimate: "no labour dollars to size the job",
  no_open_date: "no open date in the range",
  no_qualified_crew: "no crew passed the rules",
  no_run_of_free_days: "no crew free for every day",
  no_multi_day_crew: "no crew takes multi-day jobs",
  no_crew_with_room: "no free crew had room",
  no_crew_with_required_skill: "no crew has every skill the job needs",
  no_crew_passes_store: "no crew is approved for this Lowe's store",
  no_crew_passes_location: "no crew works this office",
  no_crew_passes_floor_or_furniture: "no crew can do this floor / furniture",
};

const mdy = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  return m ? `${+m[2]}/${+m[3]}/${m[1]}` : "";
};

/** "10/27/2026 11:28:16 AM" in Eastern time. */
export function favWhen(at) {
  if (typeof at !== "number") return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", month: "2-digit", day: "2-digit", year: "numeric",
    hour: "numeric", minute: "2-digit", second: "2-digit",
  }).format(new Date(at)).replace(",", "");
}

/** @returns {{when, headline, v1, fav2, failed, footer}} */
export function favCallSummary(raw) {
  const r = raw || {};
  const crew = r.installerName ? `${r.installerName} (${r.installer})` : r.installer || "";
  const range = r.startDateISO
    ? mdy(r.startDateISO) + (r.endDateISO && r.endDateISO !== r.startDateISO ? `–${mdy(r.endDateISO)}` : "")
    : "";
  const kind = r.adviceOnly ? (r.everyJob ? "advice (hard surface)" : "advice only") : "booking call";
  // (When FAV 2.0 books, the log's v1 fields describe that same booking — so check it first.)
  const v1 = r.adviceOnly
    ? "Not asked to book (advice-only call)."
    : r.booked
    ? "Not used — FAV 2.0 booked this job."
    : r.v1Reserved
    ? `Booked ${mdy(r.v1DateISO) || "a date"}, no crew.`
    : "Booked nothing (no open date).";
  const ok = r.status === "ok";
  const fav2 = ok
    ? `${r.booked ? "BOOKED" : "Would book"} ${crew}${range ? ` on ${range}` : ""}${r.workingDays > 1 ? ` (${r.workingDays} days)` : ""}.`
    : `Found nothing it could book: ${SKIP_WORDS[r.reason] || r.reason || "unknown"}.`;
  // A booking call that ended with nothing booked is the thing a researcher is looking for.
  const failed = !r.adviceOnly && !r.v1Reserved && !r.booked;
  const headline = failed
    ? `NOT booked — ${ok ? `FAV 2.0 would pick ${crew}` : SKIP_WORDS[r.reason] || r.reason || "no answer"}`
    : r.adviceOnly
    ? `${kind}: ${ok ? `${crew}${range ? ` ${range}` : ""}` : SKIP_WORDS[r.reason] || r.reason || "no suggestion"}`
    : r.booked
    ? `FAV 2.0 booked ${crew}${range ? ` ${range}` : ""}`
    : `Today's FAV booked ${mdy(r.v1DateISO)} (no crew)`;
  const uat = String(r.lambdaVersion || "").startsWith("uat-rerun") ? " · UAT re-run" : "";
  return { when: favWhen(r.at), headline, v1, fav2, failed, footer: `${kind}${uat} · FAV ${r.lambdaVersion || "?"}` };
}
